/**
 * DisclaimerGate — non-dismissible modal shown over the AppShell when
 * the user must accept the current disclaimer / terms.
 *
 * Reads `mustAcceptDisclaimer` from the auth store. Renders nothing when
 * the flag is false. When true, fetches the current disclaimer content
 * and presents it with a non-dismissible Radix Dialog (focus-trapped,
 * Escape and click-outside disabled), a checkbox guard, and Accept /
 * Decline buttons.
 *
 * Decline is one-click (`useAuth().logout()`); the explicit button copy
 * ("Decline and sign out") is the safeguard against accidental clicks.
 */

import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
} from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useAcceptDisclaimer, useCurrentDisclaimer } from '@/hooks/useDisclaimer'
import { useAuthStore } from '@/stores/authStore'

export function DisclaimerGate() {
  const mustAccept = useAuthStore((s) => s.mustAcceptDisclaimer)
  // SA-906 D7: under a View-as the acceptance is the target's own act; never
  // ask the admin to perform it on their behalf.
  const impersonating = useAuthStore((s) => s.impersonation !== null)

  if (!mustAccept || impersonating) return null
  return <DisclaimerGateInner />
}

function DisclaimerGateInner() {
  const { logout } = useAuth()
  const setMustAccept = useAuthStore((s) => s.setMustAcceptDisclaimer)
  const { data, isLoading, isError, error } = useCurrentDisclaimer()
  const acceptMutation = useAcceptDisclaimer()
  const [agreed, setAgreed] = useState(false)

  // Race: admin disabled the disclaimer between login and now. Clear the flag
  // and let the parent unmount us.
  useEffect(() => {
    if (data && data.is_enabled === false) {
      setMustAccept(false)
    }
  }, [data, setMustAccept])

  const handleAccept = () => {
    acceptMutation.mutate()
  }

  const handleDecline = () => {
    void logout()
  }

  return (
    <Modal open={true} onOpenChange={() => undefined}>
      <ModalContent
        size="lg"
        showClose={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <ModalHeader>
          <ModalTitle>{data?.title || 'Terms and Conditions'}</ModalTitle>
          <ModalDescription>
            Please review and accept the terms below to continue.
          </ModalDescription>
        </ModalHeader>

        <ModalBody className="prose prose-sm dark:prose-invert max-w-none">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Spinner size="lg" />
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">
              Failed to load terms: {(error as Error)?.message || 'unknown error'}
            </p>
          ) : data?.is_enabled && data.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content}</ReactMarkdown>
          ) : null}

          <label className="not-prose mt-6 flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
              data-testid="disclaimer-agree-checkbox"
            />
            <span className="text-sm text-foreground">
              I have read and agree to these terms.
            </span>
          </label>

          {acceptMutation.isError && (
            <p className="not-prose mt-3 text-sm text-destructive">
              {(acceptMutation.error as Error)?.message || 'Failed to record acceptance'}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={handleDecline} disabled={acceptMutation.isPending}>
            Decline and sign out
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!agreed || acceptMutation.isPending || isLoading || isError}
            isLoading={acceptMutation.isPending}
          >
            Accept
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
